import React from 'react';

// Material UI
import { Grid, styled } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const GridWithBorder = styled(Grid)(({ padding, theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '6px',
  padding: padding,
}));

const CustomGrid = styled(Grid)(({ vertical, horizontal }) => ({
  display: 'flex',
  justifyContent: horizontal,
  alignItems: vertical,
}));

const CancelIcon = styled(CancelOutlinedIcon)(({ theme }) => ({
  height: '18px',
  width: '18px',
  color: theme.palette.text.secondary,
  '&:hover': {
    cursor: 'pointer',
    color: 'var(--color-text-primary)',
  },
}));

// Base input styling
const InputBase = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '4px',
  minHeight: '36px',
  padding: '8px 12px',
  fontSize: '13px',
  position: 'relative',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.disabled,
  boxSizing: 'border-box',
}));

// Text input preview
const TextInputPreview = styled(InputBase)({});

// Long text / textarea preview
const LongTextPreview = styled(InputBase)({
  minHeight: '80px',
  alignItems: 'flex-start',
});

// Rich text preview
const RichTextPreview = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '4px',
  overflow: 'hidden',
  width: '100%',
}));

const RichTextToolbar = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '4px 8px',
  backgroundColor: theme.palette.action.hover,
  display: 'flex',
  gap: '8px',
  fontSize: '12px',
  color: theme.palette.text.secondary,
}));

const RichTextBody = styled('div')(({ theme }) => ({
  minHeight: '60px',
  padding: '8px 12px',
  fontSize: '13px',
  color: theme.palette.text.disabled,
}));

// Number input preview
const NumberInputPreview = styled(InputBase)({});

// Dropdown preview
const DropdownPreview = styled(InputBase)({
  justifyContent: 'space-between',
  cursor: 'pointer',
});

// Radio/Checkbox options container
const OptionsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
});

const OptionRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '13px',
  color: theme.palette.text.secondary,
}));

// Datetime preview
const DatetimePreview = styled(InputBase)({
  justifyContent: 'space-between',
});

const DatetimeIcons = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '4px',
  color: theme.palette.text.secondary,
}));

// List input preview
const ListPreview = styled(InputBase)({
  minHeight: '36px',
  gap: '4px',
  flexWrap: 'wrap',
});

const ListChip = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.divider,
  borderRadius: '16px',
  padding: '2px 10px',
  fontSize: '12px',
  color: theme.palette.text.secondary,
}));

const RequiredText = styled('span')({
  color: 'var(--color-text-secondary)',
  fontSize: '11px',
  marginLeft: '3px',
});

const DescriptionText = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '12px',
  marginLeft: '3px',
}));

const ColumnNameText = styled('span')({
  color: 'var(--color-text-primary)',
  fontSize: '13px',
  fontWeight: 'bold',
  width: '100%',
});

const AddNewSectionText = styled('span')({
  color: 'var(--color-text-primary)',
  fontSize: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const SectionMaxText = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '12px',
  display: 'flex',
}));

const AddIcon = styled('img')({
  color: 'var(--color-text-primary)',
  marginRight: '4px',
});

// Render appropriate input preview based on column type
const renderInputPreview = col => {
  switch (col.type) {
    case 'text':
      return <TextInputPreview>Enter text...</TextInputPreview>;

    case 'longtext':
      return <LongTextPreview>Enter long text...</LongTextPreview>;

    case 'richtext':
      return (
        <RichTextPreview>
          <RichTextToolbar>
            <span style={{ fontWeight: 'bold' }}>B</span>
            <span style={{ fontStyle: 'italic' }}>I</span>
            <span style={{ textDecoration: 'underline' }}>U</span>
            <span>|</span>
            <span>≡</span>
          </RichTextToolbar>
          <RichTextBody>Enter rich text...</RichTextBody>
        </RichTextPreview>
      );

    case 'number':
      return <NumberInputPreview>Enter number...</NumberInputPreview>;

    case 'dropdown':
      return (
        <DropdownPreview>
          <span>Select an option...</span>
          <ArrowDropDownIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        </DropdownPreview>
      );

    case 'radio':
      return (
        <OptionsContainer>
          {col.options && col.options.length > 0 ? (
            col.options.slice(0, 4).map((opt, idx) => (
              <OptionRow key={opt.id || idx}>
                <RadioButtonUncheckedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <span>{opt.value || opt.label || `Option ${idx + 1}`}</span>
              </OptionRow>
            ))
          ) : (
            <>
              <OptionRow>
                <RadioButtonUncheckedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <span>Option 1</span>
              </OptionRow>
              <OptionRow>
                <RadioButtonUncheckedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <span>Option 2</span>
              </OptionRow>
            </>
          )}
        </OptionsContainer>
      );

    case 'checkbox':
      return (
        <OptionsContainer>
          {col.options && col.options.length > 0 ? (
            col.options.slice(0, 4).map((opt, idx) => (
              <OptionRow key={opt.id || idx}>
                <CheckBoxOutlineBlankIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <span>{opt.value || opt.label || `Option ${idx + 1}`}</span>
              </OptionRow>
            ))
          ) : (
            <>
              <OptionRow>
                <CheckBoxOutlineBlankIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <span>Option 1</span>
              </OptionRow>
              <OptionRow>
                <CheckBoxOutlineBlankIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <span>Option 2</span>
              </OptionRow>
            </>
          )}
        </OptionsContainer>
      );

    case 'datetime':
      return (
        <DatetimePreview>
          <span>Select date and time...</span>
          <DatetimeIcons>
            <CalendarTodayIcon sx={{ fontSize: 18 }} />
            <AccessTimeIcon sx={{ fontSize: 18 }} />
          </DatetimeIcons>
        </DatetimePreview>
      );

    case 'list':
      return (
        <ListPreview>
          <ListChip>Item 1</ListChip>
          <ListChip>Item 2</ListChip>
          <span style={{ color: 'var(--mui-palette-text-disabled)' }}>Add items...</span>
        </ListPreview>
      );

    default:
      return <TextInputPreview>Enter value...</TextInputPreview>;
  }
};

function Preview({ data }) {
  return (
    <Grid container rowGap="24px">
      <GridWithBorder item xs={12} padding="16px">
        <Grid container rowGap="8px">
          <CustomGrid item xs={12} vertical="center" horizontal="flex-end">
            <CancelIcon />
          </CustomGrid>
          {data.columns.map((col, index) => {
            return (
              <CustomGrid item xs={12} key={col.id || index}>
                <Grid container rowGap="4px">
                  <Grid item xs={12}>
                    <ColumnNameText>
                      {col.question != '' ? col.question : 'Untitled'}
                    </ColumnNameText>
                    <RequiredText>{col.required && ' (Required)'}</RequiredText>
                  </Grid>
                  {col.description && (
                    <Grid item xs={12}>
                      <DescriptionText>{col.description}</DescriptionText>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    {renderInputPreview(col)}
                  </Grid>
                </Grid>
              </CustomGrid>
            );
          })}
        </Grid>
      </GridWithBorder>
      <GridWithBorder item xs={12} padding="6px">
        <Grid container display="flex" alignItems="center" justifyContent="center">
          <CustomGrid item xs={12} vertical="center" horizontal="center">
            <AddNewSectionText>
              <AddIcon src="/svgs/add_ad_section.svg" alt="" /> Add new section
            </AddNewSectionText>
          </CustomGrid>
        </Grid>
      </GridWithBorder>
      {data && data.numRows && (
        <Grid item xs={12}>
          <SectionMaxText>{`${data.numRows} section maximum`}</SectionMaxText>
        </Grid>
      )}
    </Grid>
  );
}

export default Preview;
